import React from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import { GridBackground } from '../components/TechUI';
import SEO from '../components/SEO';

const RefundPolicyPage = () => {
    const lastUpdated = 'February 26, 2026';

    return (
        <GridBackground className="min-h-screen">
            <SEO
                title="Refund & Cancellation Policy | Rastor AI"
                description="Refund and Cancellation Policy for Rastor AI subscriptions and digital services."
            />
            <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <Link
                    to="/"
                    className="inline-flex items-center text-slate-400 hover:text-emerald-400 transition-colors font-mono text-xs uppercase tracking-wider mb-8"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
                </Link>

                <div className="flex items-center space-x-4 mb-8">
                    <div className="w-12 h-12 border border-emerald-500/50 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                        <RotateCcw className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-white font-mono">REFUND & CANCELLATION POLICY</h1>
                        <p className="text-slate-500 text-xs font-mono uppercase mt-1">Last Updated: {lastUpdated}</p>
                    </div>
                </div>

                <div className="bg-[#0F131F]/60 border border-white/5 rounded-2xl p-8 md:p-10 space-y-8 backdrop-blur-sm">
                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">1. Overview</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            Rastor AI offers digital subscription services for AI-powered career coaching. Since our services are entirely digital in nature and delivered instantly upon purchase, please read this refund and cancellation policy carefully before making a purchase.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">2. No Refund Policy</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono mb-3">
                            All purchases on Rastor AI are <strong className="text-white">final and non-refundable</strong>. Due to the digital nature of our services — which are delivered instantly upon payment — we do not offer refunds, returns, or exchanges on any subscription plan or purchase.
                        </p>
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 font-mono text-sm">
                            <p className="text-amber-400 font-bold mb-1">⚠ Important</p>
                            <p className="text-slate-300">
                                By completing a purchase, you acknowledge that you have reviewed the features and pricing of the selected plan, and you agree that no refund will be issued after payment is processed.
                            </p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">3. Why No Refunds?</h2>
                        <ul className="list-disc list-inside text-slate-300 text-sm font-mono space-y-2 pl-4">
                            <li>Our AI-powered services (roadmaps, career analysis, assessments, resume tools) are generated and delivered instantly upon purchase.</li>
                            <li>Once generated, AI outputs cannot be "returned" as they are personalized digital content created specifically for your profile.</li>
                            <li>Subscription access to premium features is activated immediately and cannot be reversed.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">4. Exceptions</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono mb-3">
                            Refunds may be considered <strong className="text-white">only</strong> in the following exceptional cases, at our sole discretion:
                        </p>
                        <ul className="list-disc list-inside text-slate-300 text-sm font-mono space-y-1 pl-4">
                            <li>Duplicate/accidental charge due to a payment gateway error (with proof of duplicate transaction)</li>
                            <li>Payment was charged but subscription was never activated due to a technical failure on our end</li>
                        </ul>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono mt-3">
                            In such cases, contact us within <strong className="text-white">48 hours</strong> of the transaction with your Order ID and transaction proof.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">5. Cancellation Policy</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono mb-3">
                            You may cancel your subscription at any time from your account dashboard or by contacting our support team.
                        </p>
                        <ul className="list-disc list-inside text-slate-300 text-sm font-mono space-y-2 pl-4">
                            <li><strong className="text-white">Access Until Expiry:</strong> Upon cancellation, you will retain access to paid features until the end of your current subscription period.</li>
                            <li><strong className="text-white">No Partial Refunds:</strong> No refund or credit is provided for any unused portion of the subscription period.</li>
                            <li><strong className="text-white">Downgrade to Free:</strong> After your subscription period expires, your account will revert to the Free tier with limited features.</li>
                            <li><strong className="text-white">Data Retention:</strong> Your data (roadmaps, assessments, credentials) will be retained for 90 days after your subscription expires. You may request data export before this period.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">6. Shipping & Delivery Policy</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            Rastor AI is a 100% digital service. There are no physical products, and therefore no shipping or delivery is involved. Upon successful payment, access to your subscribed plan features is activated <strong className="text-white">instantly and automatically</strong>. You will receive an email confirmation with your subscription details.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">7. Contact Us</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            For billing or cancellation queries:
                        </p>
                        <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-4 font-mono text-sm">
                            <p className="text-white font-bold">Rastor AI — Support</p>
                            <p className="text-slate-400">Email: <a href="mailto:support@rastorai.com" className="text-emerald-400 hover:underline">support@rastorai.com</a></p>
                            <p className="text-slate-400">Phone: <a href="tel:+918130829610" className="text-emerald-400 hover:underline">+91 8130829610</a></p>
                            <p className="text-slate-400">Response Time: Within 24–48 hours</p>
                        </div>
                    </section>
                </div>
            </div>
        </GridBackground>
    );
};

export default RefundPolicyPage;
